import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, TrendingUpIcon, CheckCircleIcon } from 'lucide-react';
export function HeroSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-74">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-75">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-76">
          {/* Left Content */}
          <motion.div className="max-w-2xl" variants={containerVariants} initial="hidden" animate="visible" data-id="element-77">
            <motion.div variants={itemVariants} className="mb-6" data-id="element-78">
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[rgba(255,107,43,0.1)] text-accent font-medium text-sm" data-id="element-79">
                <MapPinIcon className="w-4 h-4" data-id="element-80" />
                Expertos en Marketing Local
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 tracking-tight" data-id="element-81">
              Tu negocio merece estar lleno
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-lg" data-id="element-82">
              Creamos tu página web y manejamos tus redes sociales para que más
              clientes te encuentren. Sin tecnicismos. Sin complicaciones.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4" data-id="element-83">
              <a href="#contacto" className="inline-flex items-center justify-center px-8 py-4 bg-accent text-white font-medium rounded-xl hover:bg-[#e55a1f] transition-colors shadow-lg shadow-accent/20" data-id="element-84">
                Quiero más clientes &rarr;
              </a>
              <a href="#casos-de-exito" className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-primary text-primary font-medium rounded-xl hover:bg-primary/5 transition-colors" data-id="element-85">
                Ver ejemplos
              </a>
            </motion.div>
          </motion.div>

          {/* Right Content - Visuals */}
          <motion.div className="relative lg:ml-10 mt-10 lg:mt-0" initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut',
          delay: 0.2
        }} data-id="element-86">
            {/* Browser Mockup */}
            <div className="relative z-10 rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden" data-id="element-87">
              {/* Browser Header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2" data-id="element-88">
                <div className="w-3 h-3 rounded-full bg-[#FF5F56]" data-id="element-89" />
                <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" data-id="element-90" />
                <div className="w-3 h-3 rounded-full bg-[#27C93F]" data-id="element-91" />
                <div className="ml-4 flex-1 bg-white rounded-md h-6 border border-gray-200" data-id="element-92" />
              </div>
              {/* Browser Content (Barbershop Mockup) */}
              <div className="bg-dark text-white p-6 sm:p-8 aspect-[4/3] flex flex-col justify-center items-center text-center relative overflow-hidden" data-id="element-93">
                <img src="https://images.unsplash.com/photo-1621605815971-fbc98d665033?auto=format&fit=crop&w=1200&q=80" alt="Interior de barbería moderna" className="absolute inset-0 w-full h-full object-cover" data-id="element-94" />
                <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/70 to-dark/40" data-id="element-95" />
                <div className="relative z-10" data-id="element-96">
                  <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-2 text-white" data-id="element-97">
                    Barbería Don Carlos
                  </h3>
                  <p className="text-gray-400 mb-6 text-sm sm:text-base" data-id="element-98">
                    Cortes clásicos y modernos.
                  </p>
                  <div className="inline-flex px-6 py-2 bg-white text-dark rounded-xl font-medium text-sm" data-id="element-99">
                    Reservar Cita
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Glassmorphism Cards */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut'
          }} className="absolute left-2 sm:-left-12 top-20 z-20 backdrop-blur-md bg-white/70 border border-white/40 shadow-xl rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3" data-id="element-100">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600" data-id="element-101">
                <TrendingUpIcon className="w-5 h-5" data-id="element-102" />
              </div>
              <div data-id="element-103">
                <p className="text-xs text-gray-500 font-medium" data-id="element-104">Resultados</p>
                <p className="text-sm font-bold text-dark" data-id="element-105">+40% clientes</p>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            repeat: Infinity,
            duration: 5,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute right-2 sm:-right-8 bottom-10 sm:bottom-16 z-20 backdrop-blur-md bg-white/70 border border-white/40 shadow-xl rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3" data-id="element-106">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-primary" data-id="element-107">
                <CheckCircleIcon className="w-5 h-5" data-id="element-108" />
              </div>
              <div data-id="element-109">
                <p className="text-xs text-gray-500 font-medium" data-id="element-110">Entrega</p>
                <p className="text-sm font-bold text-dark" data-id="element-111">Lista en 1 día</p>
              </div>
            </motion.div>

            {/* Background decorative blob */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-secondary rounded-full blur-3xl -z-10 opacity-60" data-id="element-112" />
          </motion.div>
        </div>
      </div>
    </section>;
}